import { KlimAxiosRequestConfig, axios } from "utils";

import { TIMEOUT_LONG_RUNNING, PUBLIC_SOIL_SIGHT_URL as endpoint } from "consts";

import {
  CalculateAnonymousForecastRequestBody,
  CalculateAnonymousForecastResponse,
  CmcMixCopyItem,
  CmcMixCopyRequest,
  CmcScenarioRecommendation,
  CreateSoilSightProjectBody,
  CreateSoilSightProjectResponse,
  CreateSoilSightScenarioBody,
  CreateSoilSightScenarioResponse,
  GenerateSoilSightDetailedAnalysisResponse,
  GenerateSoilSightOutcomesResponse,
  PrepareAnonymousForecastRequestBody,
  PrepareAnonymousForecastResponse,
  ReplaceCropYearsBody,
  ReplaceCropYearsResponse,
  ResilienceInsight,
  ResilienceInsightRequest,
  SilverServiceVersion,
  SoilSightCoverCropTiming,
  SoilSightCropDefaultsByCropId,
  SoilSightFieldSoil,
  SoilSightNonCropDefaults,
  SoilSightProject,
  SoilSightProjectResponse,
  SoilSightScenario,
  SoilSightScenarioDetail,
  UpdateProjectSettingsRefinementsResponse,
  UpdateSettingsRefinementsBody,
  UpdateSoilSightScenarioBody,
} from "models";

export const generateSoilSightOutcomes = async (projectId: string, config?: KlimAxiosRequestConfig): Promise<GenerateSoilSightOutcomesResponse> =>
  axios.post(`${endpoint}/run`, { projectId }, config);

export const calculateAnonymousForecast = async (
  body: CalculateAnonymousForecastRequestBody,
  config?: KlimAxiosRequestConfig
): Promise<CalculateAnonymousForecastResponse> => axios.post(`${endpoint}/run-anon`, body, config);

export const prepareAnonymousForecast = async (
  body: PrepareAnonymousForecastRequestBody,
  config?: KlimAxiosRequestConfig
): Promise<PrepareAnonymousForecastResponse> => axios.post(`${endpoint}/prepare-anon`, body, config);

export const emailSoilSightLink = async (): Promise<{ email: string }> => axios.post(`${endpoint}/email-link`);

export const listPublicSilverVersions = async (): Promise<SilverServiceVersion[]> => axios.get(`${endpoint}/silver-versions`);

export const getSoilSightProject = async (soilSightProjectId: string): Promise<SoilSightProjectResponse> => axios.get(`${endpoint}/${soilSightProjectId}`);

export const getSoilSightFieldSoil = async (soilSightProjectId: string): Promise<SoilSightFieldSoil> =>
  axios.get(`${endpoint}/${soilSightProjectId}/soil-properties`);

export const getSoilSightCoverCropTiming = async (soilSightProjectId: string): Promise<SoilSightCoverCropTiming> =>
  axios.get(`${endpoint}/${soilSightProjectId}/cover-crop-timing`);

export const createSoilSightProject = async (body: CreateSoilSightProjectBody): Promise<CreateSoilSightProjectResponse> => axios.post(`${endpoint}`, body);

export const deleteSoilSightProject = async (soilSightProjectId: string): Promise<{ success: true }> => axios.delete(`${endpoint}/${soilSightProjectId}`);

// Resolves the mounted silver version server-side, which is a live (uncached) Silver Service call.

export const updateSoilSightProjectSettingsRefinements = async (
  soilSightProjectId: string,
  body: UpdateSettingsRefinementsBody
): Promise<UpdateProjectSettingsRefinementsResponse> =>
  axios.put(`${endpoint}/${soilSightProjectId}/settings-refinements`, body, { timeout: TIMEOUT_LONG_RUNNING });

export const listSoilSightScenarios = async (soilSightProjectId: string): Promise<SoilSightScenario[]> =>
  axios.get(`${endpoint}/${soilSightProjectId}/scenarios`);

export const createSoilSightScenario = async (soilSightProjectId: string, body: CreateSoilSightScenarioBody): Promise<CreateSoilSightScenarioResponse> =>
  axios.post(`${endpoint}/${soilSightProjectId}/scenarios`, body);

export const getSoilSightScenario = async (scenarioId: string): Promise<SoilSightScenarioDetail> => axios.get(`${endpoint}/scenarios/${scenarioId}`);

export const updateSoilSightScenario = async (scenarioId: string, body: UpdateSoilSightScenarioBody): Promise<SoilSightScenarioDetail> =>
  axios.put(`${endpoint}/scenarios/${scenarioId}`, body);

export const deleteSoilSightScenario = async (scenarioId: string): Promise<void> => axios.delete(`${endpoint}/scenarios/${scenarioId}`);

// On the import/CTA critical path alongside the refinements PUT, so it needs the same headroom.

export const replaceSoilSightCropYears = async (scenarioId: string, body: ReplaceCropYearsBody): Promise<ReplaceCropYearsResponse> =>
  axios.put(`${endpoint}/scenarios/${scenarioId}/crop-years`, body, { timeout: TIMEOUT_LONG_RUNNING });

// Empty cropIds fetches the full catalogue once on mount instead of per drawer pick.

export const getSoilSightCropDatabase = async (cropIds?: string[]): Promise<SoilSightCropDefaultsByCropId> =>
  axios.get(`${endpoint}/crop-database`, {
    params: cropIds && cropIds.length > 0 ? { cropIds: cropIds.join(",") } : undefined,
  });

export const getSoilSightNonCropDatabase = async (): Promise<SoilSightNonCropDefaults> => axios.get(`${endpoint}/non-crop-database`);

export const getSoilSightCmcRecommendation = async (scenarioId: string): Promise<CmcScenarioRecommendation[]> =>
  axios.get(`${endpoint}/scenarios/${scenarioId}/cmc-recommendation`);

export const generateCmcMixCopy = ({ requestBody, signal }: { requestBody: CmcMixCopyRequest; signal?: AbortSignal }): Promise<CmcMixCopyItem[]> =>
  axios.post(`${endpoint}/ai/cmc-mix-content`, requestBody, { signal });

export const generateResilienceInsight = ({
  requestBody,
  signal,
}: {
  requestBody: ResilienceInsightRequest;
  signal?: AbortSignal;
}): Promise<ResilienceInsight> => axios.post(`${endpoint}/ai/resilience-insight`, requestBody, { signal });

export const generateSoilSightDetailedAnalysis = ({
  body,
  signal,
}: {
  body: { projectId: string; scenarioId: string };
  signal?: AbortSignal;
}): Promise<GenerateSoilSightDetailedAnalysisResponse> => axios.post(`${endpoint}/ai/detailed-analysis`, body, { signal });

export const listSoilSightProjects = async (klimFarmId: string): Promise<SoilSightProject[]> => axios.get(`${endpoint}`, { params: { farmId: klimFarmId } });

export const renameSoilSightProject = async (soilSightProjectId: string, name: string): Promise<{ success: true }> =>
  axios.patch(`${endpoint}/${soilSightProjectId}`, { name });

export const duplicateSoilSightProject = async (soilSightProjectId: string, name: string): Promise<{ success: true; soilSightProjectId: string }> =>
  axios.post(`${endpoint}/${soilSightProjectId}/duplicate`, { name });
